/**
 * RevenueCalendar
 *
 * Full-screen animated calendar overlay triggered by clicking the KPI progress bar.
 * Shows cash collected per day as heat-mapped day cells. Click a day to see full
 * funnel metrics for that day.
 */

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface DayMetrics {
  date: string;
  totalCash: number;
  newCash: number;
  mrrCash: number;
  closes: number;
  mrrs: number;
  callsTaken: number;
  callsDue: number;
  dials: number;
  callsBooked: number;
  apps: number;
  optins: number;
}

interface CalendarApiResponse {
  success: boolean;
  data?: {
    year: number;
    month: number;
    days: DayMetrics[];
  };
}

interface RevenueCalendarProps {
  open: boolean;
  onClose: () => void;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function fmtCash(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  if (n > 0) return `$${Math.round(n)}`;
  return "—";
}

function fmtDate(dateStr: string): string {
  // Add noon time to avoid timezone shift
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

export function RevenueCalendar({ open, onClose }: RevenueCalendarProps) {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
  const [calData, setCalData] = useState<Record<string, DayMetrics>>({});
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const fetchData = useCallback(async (year: number, month: number) => {
    setLoading(true);
    setCalData({});
    try {
      const res = await fetch(`/api/calendar/metrics?year=${year}&month=${month}`);
      const json = await res.json() as CalendarApiResponse;
      if (json.success && json.data) {
        const map: Record<string, DayMetrics> = {};
        for (const d of json.data.days) map[d.date] = d;
        setCalData(map);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      void fetchData(viewYear, viewMonth);
      setSelectedDate(null);
    }
  }, [open, viewYear, viewMonth, fetchData]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const prevMonth = () => {
    if (viewMonth === 1) { setViewYear(y => y - 1); setViewMonth(12); }
    else setViewMonth(m => m - 1);
    setSelectedDate(null);
  };

  const nextMonth = () => {
    if (viewMonth === 12) { setViewYear(y => y + 1); setViewMonth(1); }
    else setViewMonth(m => m + 1);
    setSelectedDate(null);
  };

  const firstDayOfWeek = new Date(viewYear, viewMonth - 1, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
  const todayStr = now.toISOString().split("T")[0]!;

  const maxCash = Math.max(...Object.values(calData).map(d => d.totalCash), 1);

  const getCellBg = (cash: number, isSelected: boolean): string => {
    if (isSelected) return "rgba(97, 93, 255, 0.25)";
    if (cash <= 0) return "rgba(255,255,255,0.03)";
    const ratio = Math.min(cash / maxCash, 1);
    const alpha = 0.1 + ratio * 0.35;
    return `rgba(34, 197, 94, ${alpha})`;
  };

  const selectedMetrics = selectedDate ? calData[selectedDate] ?? null : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="calendar-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6"
          style={{ backdropFilter: "blur(20px)", background: "rgba(0,0,0,0.72)" }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            key="calendar-panel"
            initial={{ opacity: 0, scale: 0.93, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.93, y: 20 }}
            transition={{ duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }}
            className="relative w-full max-w-3xl rounded-2xl border border-border overflow-hidden"
            style={{
              background: "linear-gradient(160deg, rgba(18,18,32,0.99) 0%, rgba(10,10,22,0.99) 100%)",
              boxShadow: "0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)",
            }}
          >
            {/* Top glow line */}
            <div
              className="absolute top-0 left-0 right-0 h-px"
              style={{ background: "linear-gradient(90deg, transparent 0%, var(--brand-gradient-start) 30%, var(--brand-gradient-end) 70%, transparent 100%)" }}
            />

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div>
                <h2 className="text-base font-bold text-white tracking-tight">Revenue Calendar</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Cash collected per day — click any day for full breakdown</p>
              </div>

              <div className="flex items-center gap-2">
                {/* Month navigation */}
                <div className="flex items-center gap-0.5 bg-muted/10 border border-border rounded-lg p-1">
                  <button
                    type="button"
                    onClick={prevMonth}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-all"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm font-semibold text-white px-3 min-w-[130px] text-center select-none">
                    {MONTH_NAMES[viewMonth - 1]} {viewYear}
                  </span>
                  <button
                    type="button"
                    onClick={nextMonth}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-all"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Close */}
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 rounded-lg text-muted-foreground hover:text-white bg-muted/10 hover:bg-muted/20 border border-border transition-all"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Calendar grid */}
            <div className="px-5 pb-5">
              {/* Day-of-week headers */}
              <div className="grid grid-cols-7 gap-1 mb-1.5">
                {DAY_LABELS.map(label => (
                  <div key={label} className="text-center py-1 text-[9px] font-bold text-muted-foreground uppercase tracking-widest">
                    {label}
                  </div>
                ))}
              </div>

              {/* Day cells */}
              {loading ? (
                <div className="flex items-center justify-center h-44 text-muted-foreground text-sm">
                  <motion.div
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    Loading...
                  </motion.div>
                </div>
              ) : (
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                    <div key={`pad-${i}`} />
                  ))}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const dateStr = `${viewYear}-${String(viewMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                    const m = calData[dateStr];
                    const cash = m?.totalCash ?? 0;
                    const isSelected = selectedDate === dateStr;
                    const isToday = dateStr === todayStr;
                    const hasCash = cash > 0;

                    return (
                      <motion.button
                        key={dateStr}
                        type="button"
                        whileHover={{ scale: 1.05, transition: { duration: 0.12 } }}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                        className="relative rounded-xl text-left transition-colors"
                        style={{
                          background: getCellBg(cash, isSelected),
                          border: isSelected
                            ? "1px solid var(--brand-primary)"
                            : isToday
                            ? "1px solid rgba(255,255,255,0.2)"
                            : "1px solid rgba(255,255,255,0.05)",
                          minHeight: "58px",
                          padding: "6px 7px",
                        }}
                      >
                        <span
                          className="text-[11px] font-bold leading-none block"
                          style={{ color: isToday ? "var(--brand-primary)" : "rgba(255,255,255,0.55)" }}
                        >
                          {day}
                        </span>
                        {hasCash && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="block mt-1.5 text-[10px] font-semibold leading-none"
                            style={{ color: isSelected ? "rgba(255,255,255,0.9)" : "rgba(74, 222, 128, 0.9)" }}
                          >
                            {fmtCash(cash)}
                          </motion.span>
                        )}
                        {isToday && (
                          <div className="absolute bottom-1.5 right-1.5 w-1 h-1 rounded-full" style={{ background: "var(--brand-primary)" }} />
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Day detail panel */}
            <AnimatePresence>
              {selectedMetrics && selectedDate && (
                <motion.div
                  key="day-detail"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="overflow-hidden"
                  style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <div className="px-5 py-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-bold text-white">{fmtDate(selectedDate)}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Full funnel breakdown</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedDate(null)}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground/60 hover:bg-muted/10 transition-all"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Funnel metrics row */}
                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5 mb-1.5">
                      {[
                        { label: "Dials", value: selectedMetrics.dials || 0, color: "var(--brand-secondary)" },
                        { label: "Optins", value: selectedMetrics.optins || 0, color: "var(--brand-accent)" },
                        { label: "Apps", value: selectedMetrics.apps || 0, color: "var(--brand-primary)" },
                        { label: "Booked", value: selectedMetrics.callsBooked || 0, color: "var(--brand-ring)" },
                        { label: "Calls", value: selectedMetrics.callsTaken || 0, color: "rgba(234,179,8,0.9)" },
                        { label: "Due", value: selectedMetrics.callsDue || 0, color: "rgba(255,255,255,0.4)" },
                        { label: "Closes", value: selectedMetrics.closes || 0, color: "rgba(74,222,128,0.9)" },
                        { label: "MRRs", value: selectedMetrics.mrrs || 0, color: "rgba(52,211,153,0.9)" },
                      ].map(({ label, value, color }) => (
                        <motion.div
                          key={label}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                          className="rounded-xl text-center py-2.5 px-1"
                          style={{
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.07)",
                          }}
                        >
                          <div className="text-base font-bold leading-none" style={{ color }}>
                            {value}
                          </div>
                          <div className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mt-1.5">
                            {label}
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Cash breakdown row */}
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { label: "New Cash", value: selectedMetrics.newCash, color: "rgba(74,222,128,0.9)" },
                        { label: "MRR Cash", value: selectedMetrics.mrrCash, color: "rgba(52,211,153,0.9)" },
                        { label: "Total Cash", value: selectedMetrics.totalCash, color: "rgba(255,255,255,0.9)", bold: true },
                      ].map(({ label, value, color, bold }) => (
                        <motion.div
                          key={label}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2, delay: 0.05 }}
                          className="flex items-center justify-between rounded-xl px-3 py-2.5"
                          style={{
                            background: bold ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.04)",
                            border: bold ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(255,255,255,0.07)",
                          }}
                        >
                          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
                          <span className="text-sm font-bold" style={{ color }}>{fmtCash(value)}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
